import { useEffect, useState } from "react";
import { api, type AdminUser } from "@/lib/api";

export default function AdminUsers() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [newEmail, setNewEmail] = useState("");
  const [busy, setBusy] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const r = await api.listAdminUsers();
      setUsers(r.users);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function flash(m: string) { setMsg(m); setErr(null); setTimeout(() => setMsg(null), 4000); }

  async function addUser(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await api.createAdminUser(newEmail.trim());
      setNewEmail("");
      flash("Account created. Set a password for it below so they can sign in.");
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not add account");
    } finally {
      setBusy(false);
    }
  }

  async function setPassword(u: AdminUser) {
    const pw = prompt(`Set a password for ${u.email}\n(at least 10 characters):`);
    if (pw == null) return;
    if (pw.length < 10) { setErr("Password must be at least 10 characters."); return; }
    setBusy(true); setErr(null);
    try {
      await api.setAdminPassword(u.id, pw);
      flash(`Password set for ${u.email}.`);
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not set password");
    } finally {
      setBusy(false);
    }
  }

  async function toggleActive(u: AdminUser) {
    setBusy(true); setErr(null);
    try {
      await api.setAdminActive(u.id, !u.is_active);
      flash(`${u.email} ${u.is_active ? "deactivated" : "activated"}.`);
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not update account");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="max-w-3xl">
      <h1 className="text-xl font-serif mb-1">Admin accounts</h1>
      <p className="help mb-4">
        Each person signs in with their own email + password. Passwords are set here (admin-managed) —
        add an account, then set a password and share it with that person to change on first login.
        Accounts without a password can still use the email sign-in link if their address is allow-listed.
      </p>

      <form onSubmit={addUser} className="flex flex-wrap items-end gap-3 mb-5">
        <div className="grow min-w-[220px]">
          <label className="label">Add an account (email)</label>
          <input className="input" type="email" placeholder="name@ibewlocal1.org"
                 value={newEmail} onChange={(e) => setNewEmail(e.target.value)} required />
        </div>
        <button className="btn-primary" disabled={busy}>Add account</button>
      </form>

      {msg && <div className="mb-3 text-sm bg-green-50 border border-green-200 text-green-800 rounded p-2">{msg}</div>}
      {err && <div className="mb-3 text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded p-2">{err}</div>}

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-stone-100 text-left">
            <tr>
              <th className="px-3 py-2">Email</th>
              <th className="px-3 py-2">Password</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Last sign-in</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="px-3 py-6 text-museum-muted">Loading…</td></tr>}
            {!loading && users.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-museum-muted">No accounts yet.</td></tr>}
            {!loading && users.map((u) => (
              <tr key={u.id} className="border-t border-stone-200">
                <td className="px-3 py-2 font-medium">{u.email}</td>
                <td className="px-3 py-2">
                  {u.has_password
                    ? <span className="inline-block px-2 py-0.5 rounded text-xs bg-green-100 text-green-800">Set</span>
                    : <span className="inline-block px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-800">Not set</span>}
                </td>
                <td className="px-3 py-2">
                  {u.is_active
                    ? <span className="inline-block px-2 py-0.5 rounded text-xs bg-green-100 text-green-800">Active</span>
                    : <span className="inline-block px-2 py-0.5 rounded text-xs bg-stone-200 text-stone-700">Inactive</span>}
                </td>
                <td className="px-3 py-2 text-museum-muted">
                  {u.last_login_at ? new Date(u.last_login_at + "Z").toLocaleString() : "—"}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <button className="underline mr-3" disabled={busy} onClick={() => setPassword(u)}>
                    {u.has_password ? "Reset password" : "Set password"}
                  </button>
                  <button className="underline" disabled={busy} onClick={() => toggleActive(u)}>
                    {u.is_active ? "Deactivate" : "Activate"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
